import { useEffect, useId } from 'react';
import type { ReactNode } from 'react';
import { Check, Loader2, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface Props {
  abierto: boolean;
  alCerrar: () => void;
  titulo: string;
  subtitulo?: string | undefined;
  icono?: LucideIcon | undefined;
  ancho?: 'estrecho' | 'medio' | 'ancho' | 'amplio' | 'ventanilla' | 'pantalla';

  cerrarAlPulsarFuera?: boolean;
  children: ReactNode;
}

const ANCHOS = {
  estrecho: 'max-w-md',
  medio: 'max-w-lg',
  ancho: 'max-w-2xl',
  amplio: 'max-w-3xl',

  ventanilla: 'max-w-5xl',

  pantalla: 'max-w-[92rem]',
} as const;

export function Modal({
  abierto,
  alCerrar,
  titulo,
  subtitulo,
  icono: Icono,
  ancho = 'medio',
  cerrarAlPulsarFuera = true,
  children,
}: Props) {
  const idTitulo = useId();

  useEffect(() => {
    if (!abierto) {
      return;
    }
    function alPulsar(evento: KeyboardEvent) {
      if (evento.key === 'Escape') {
        alCerrar();
      }
    }
    document.addEventListener('keydown', alPulsar);
    return () => document.removeEventListener('keydown', alPulsar);
  }, [abierto, alCerrar]);

  useEffect(() => {
    if (!abierto) {
      return;
    }
    const previo = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previo;
    };
  }, [abierto]);

  if (!abierto) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-armazon-900/40 backdrop-blur-sm"
        onClick={cerrarAlPulsarFuera ? alCerrar : undefined}
        aria-hidden
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className={`relative flex max-h-[95dvh] w-full ${ANCHOS[ancho]} flex-col overflow-hidden rounded-2xl bg-white shadow-2xl`}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-armazon-100 px-6 py-4">
          <div className="flex min-w-0 items-center gap-3">
            {Icono && (
              <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-marca-50 text-marca-600">
                <Icono className="size-5" aria-hidden />
              </span>
            )}
            <div className="min-w-0">
              <h2 id={idTitulo} className="text-base font-bold uppercase text-armazon-900">
                {titulo}
              </h2>
              {subtitulo && (
                <p className="text-xs uppercase tracking-wide text-armazon-500">{subtitulo}</p>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={alCerrar}
            aria-label="Cerrar"
            className="grid size-8 shrink-0 place-items-center rounded-lg text-armazon-400 transition-colors hover:bg-armazon-100 hover:text-armazon-700"
          >
            <X className="size-[18px]" aria-hidden />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

export function Campo({
  etiqueta,
  error,
  ayuda,
  requerido = false,
  children,
}: {
  etiqueta: string;
  error?: string | undefined;
  ayuda?: string | undefined;
  requerido?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-semibold uppercase text-armazon-800">
        {etiqueta}
        {requerido && <span className="ml-0.5 text-alerta">*</span>}
      </span>
      {children}
      {error ? (
        <span role="alert" className="mt-1.5 block text-xs text-alerta">
          {error}
        </span>
      ) : (
        ayuda && (
          <span className="mt-1 block text-[11px] leading-snug text-armazon-500">{ayuda}</span>
        )
      )}
    </label>
  );
}

export const claseEntrada =
  'w-full rounded-lg border border-armazon-200 bg-white px-3 py-2.5 text-sm text-armazon-900 outline-none transition-colors placeholder:text-armazon-400 focus:border-marca-400 focus:ring-2 focus:ring-marca-500/20';

export function AccionesDelFormulario({
  alCancelar,
  enviando,
  textoEnviar = 'Guardar',
}: {
  alCancelar: () => void;
  enviando: boolean;
  textoEnviar?: string;
}) {
  return (
    <div className="sticky bottom-0 -mx-6 -mb-5 mt-6 flex items-center justify-end gap-2 border-t border-armazon-100 bg-white px-6 py-4">
      <button
        type="button"
        onClick={alCancelar}
        title="Cancelar"
        aria-label="Cancelar"
        className="grid size-10 place-items-center rounded-lg text-armazon-500 transition-colors hover:bg-armazon-100 hover:text-armazon-700"
      >
        <X className="size-[18px]" aria-hidden />
      </button>
      <button
        type="submit"
        disabled={enviando}
        title={textoEnviar}
        aria-label={textoEnviar}
        className="grid size-10 place-items-center rounded-lg bg-marca-600 text-white transition-colors hover:bg-marca-700 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {enviando ? (
          <Loader2 className="size-[18px] animate-spin" aria-hidden />
        ) : (
          <Check className="size-[18px]" aria-hidden />
        )}
      </button>
    </div>
  );
}
